import { FC, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import {
  AtendimentoType,
  ButtonType,
  DocumentoPessoaLabels,
  DocumentoPessoaType,
  GenderType,
  MaskType,
  ModalidadeType,
  notificacaoOptions,
  NotificacaoType,
  getApiErrorData,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import DOMPurify from 'dompurify'
import {
  Button,
  FormDateInput,
  FormInput,
  FormRadio,
  FormSelect,
  InfoAlert,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
  useToast,
} from '../../../../../../components'
import { useFormik } from 'formik'
import {
  createPaciente,
  getPacienteByPessoaId,
  vincularPacienteEmpresa,
} from '../../../../paciente-management/pacientes-list/core/_requests'
import { AtendimentoDtoCreate } from '../../../../atendimento/core/_models'
import { getAtendimentoById } from '../../../../atendimento/core/_requests'
import {
  abrirInterconsulta,
  enviarFormularioSolicitacao,
  getLotacoesSolicitante,
} from '../../core/_requests'
import { formulariosEmAberto, temFormularioMarcado } from '../../core/formulariosInterconsulta'
import { descreveLotacaoElegivel, lotacoesDaAmbiguidade } from '../../core/lotacaoSolicitante'
import { LotacaoElegivel } from '../../core/_models'
import { FormularioAtendimentoList } from '../../../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import { SelecionarFormularioModal } from '../../../../atendimento_medicos/atendimento-video/modal/SelecionarFormularioModal'
import {
  PacienteDtoAdd,
  PacienteDtoCreate,
} from '../../../../paciente-management/pacientes-list/core/_models'
import { interconsultaSchema } from './validation/interconsultaSchema'
import { getUnidades } from '../../../../unidades/unidades-list/core/_requests'
import { descreveLotacao } from '../../../../funcionarios/funcionarios-list/core/descreveLotacao'
import { PessoaDto, useAuth, UsuarioDtoUpdate } from '../../../../../auth'
import { FormularioModal } from '../../../../atendimento_medicos/atendimento-video/modal/formulario-answer/FormularioModal'
import { IInfo } from '../../../../../../components/Modal/SearchPersonModal'
import {
  getFormularioAtendimento,
  getFormularioResolvido,
} from '../../../../avaliacoes/avaliacoes-list/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import { updateUserAutenticado } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_requests'

/**
 * Erro da abertura com a lista do que falta. As exigências RATC de Núcleo credenciado na SES/MG
 * voltam num 422 só, com um título genérico em `message` e um item por campo em `errors` (CNS do
 * paciente, CNES, CBO e unidade da lotação…). O `showError` mostra só o título quando há `message`,
 * e aí o solicitante não saberia o que corrigir.
 */
const mostrarErroAbertura = (error: unknown) => {
  const dados = getApiErrorData(error)
  const pendencias = Object.values(dados?.errors ?? {}).flat()
  if (!pendencias.length) return showError(error)

  const itens = pendencias.map((pendencia) => `<li>${pendencia}</li>`).join('')
  return showAlert({
    title: dados?.message || 'Não foi possível abrir a interconsulta',
    // Texto do backend vai para HTML: sanitizar.
    html: DOMPurify.sanitize(`<ul class="text-start mb-0">${itens}</ul>`),
    icon: 'error',
  })
}

const CAMINHO_CADASTRO_LOTACAO =
  'Para corrigir, peça ao administrador da instituição que cadastre sua lotação em Funcionários › Lotações do profissional.'

type IProps = {
  info: IInfo
  pessoa?: PessoaDto
}
export const InterconsultaModal: FC<IProps> = ({ info, pessoa }) => {
  const { closeModal, showModal } = useModal()
  const { showToast } = useToast()
  const { currentUser } = useAuth()
  const { t } = useT()

  const [pacienteCriado, setPacienteCriado] = useState<number>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [optionsUnidades, setOptionsUnidades] = useState<IOption<number>[]>([])
  const [empresaSelecionada, setEmpresaSelecionada] = useState<number>()
  const [unidadeSelecionada, setUnidadeSelecionada] = useState<number>()

  // Lotação do solicitante (RATC): o backend carimba sozinho quando há uma única vigente na
  // unidade; com mais de uma, o solicitante escolhe. A rota devolve as lotações do vínculo de
  // solicitante (ou profissional de saúde) do usuário logado na empresa — lista vazia quando não há
  // vínculo. Sem lotação, o backend abre sem carimbo e só registra em log; com mais de uma e nenhuma
  // informada, recusa com a lista (abaixo). Por isso a tela avisa antes de abrir e confere o
  // carimbo depois (LO-1420: nada de falha silenciosa).
  const {
    data: lotacoes,
    isSuccess: lotacoesConsultadas,
    isError: lotacoesIndisponiveis,
  } = useQuery(
    ['lotacoes-solicitante', empresaSelecionada],
    () => getLotacoesSolicitante(Number(empresaSelecionada)),
    { enabled: !!empresaSelecionada, retry: false, staleTime: 60_000 }
  )
  // Quando a tela não previu a ambiguidade (ex.: a consulta acima falhou), o backend recusa a
  // abertura com 422 LOTACAO_SOLICITANTE_AMBIGUA e a lista das lotações elegíveis: ela passa a ser
  // a fonte do seletor, até trocar a unidade.
  const [lotacoesElegiveis, setLotacoesElegiveis] = useState<LotacaoElegivel[]>()
  const opcoesLotacao = useMemo<IOption<number>[]>(
    () =>
      lotacoesElegiveis
        ? lotacoesElegiveis.map((l) => ({ value: l.id, label: descreveLotacaoElegivel(l) }))
        : (lotacoes ?? [])
            .filter(
              (l) => l.vigente && (!unidadeSelecionada || l.unidade?.id === unidadeSelecionada)
            )
            .map((l) => ({ value: l.id, label: descreveLotacao(l) })),
    [lotacoesElegiveis, lotacoes, unidadeSelecionada]
  )
  const exigeLotacao = opcoesLotacao.length > 1
  const semLotacao = lotacoesConsultadas && opcoesLotacao.length === 0

  const initialValues = {
    pessoaId: pessoa?.id,

    empresa: undefined as IOption<number> | undefined,
    modalidade: ModalidadeType.Parecer,
    unidade: undefined as IOption<number> | undefined,
    lotacaoSolicitante: undefined as IOption<number> | undefined,
    especialidade: undefined as IOption<number> | undefined,

    // paciente
    primeiroNome: info?.primeiroNome || pessoa?.primeiroNome || '',
    sobreNome: info?.sobreNome || pessoa?.sobreNome || '',
    cpf: info?.documento.tipo === DocumentoPessoaType.Cpf ? info?.documento.numero : '',
    cns: info?.documento.tipo === DocumentoPessoaType.Cns ? info?.documento.numero : '',

    sexo: pessoa
      ? {
          label: pessoa.sexo === GenderType.Feminino ? 'Feminino' : 'Masculino',
          value: pessoa.sexo,
        }
      : (undefined as IOption<GenderType> | undefined),
    nascimento: info?.dataNascimento || pessoa?.dataNascimento || '',
    passaporte:
      info?.documento.tipo === DocumentoPessoaType.Passaporte ? info?.documento.numero : '',

    bilheteIdentidadeAngola:
      info?.documento.tipo === DocumentoPessoaType.BilheteIdentidadeAngola
        ? info?.documento.numero
        : '',

    documento: info?.documento.tipo
      ? { label: DocumentoPessoaLabels[info?.documento.tipo], value: info?.documento.tipo }
      : {
          label: DocumentoPessoaLabels[DocumentoPessoaType.Cpf],
          value: DocumentoPessoaType.Cpf,
        },
    tipoNotificacao: null as NotificacaoType | null,
    telefone: '',
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(
      () => interconsultaSchema(t, !!pacienteCriado, exigeLotacao),
      [t, pacienteCriado, exigeLotacao]
    ),

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (!pacienteCriado) {
          if (pessoa && values.empresa?.value) {
            try {
              const pacienteExist = await getPacienteByPessoaId(
                Number(values.empresa?.value),
                pessoa.id
              )
              if (pacienteExist) setPacienteCriado(Number(pacienteExist.id))
            } catch (error) {
              const paciente: PacienteDtoAdd = {
                tipoDocumento: info?.documento.tipo as DocumentoPessoaType,
                numeroDocumento: info?.documento.numero as string,
              }
              const p = await vincularPacienteEmpresa(values.empresa?.value as number, paciente)
              setPacienteCriado(Number(p.id))
            }
            setSubmitting(false)
            return
          }

          const pacienteCreate: PacienteDtoCreate = {
            primeiroNome: values.primeiroNome,
            sobreNome: values.sobreNome,
            cpf: values.cpf || undefined,
            cns: values.cns || undefined,
            passaporte: values.passaporte || undefined,
            bilheteIdentidadeAngola: values.bilheteIdentidadeAngola || undefined,
            sexo: values.sexo?.value as GenderType,
            nascimento: values.nascimento,
          }

          const paciente = await createPaciente(
            formik.values.empresa?.value as number,
            pacienteCreate
          )
          setPacienteCriado(Number(paciente?.id))

          setSubmitting(false)
          return
        }

        if (shouldFillTelefone) {
          const usuario: UsuarioDtoUpdate = {
            id: Number(currentUser?.id),
            telefone: values.telefone,
          }
          await updateUserAutenticado(usuario)
        }

        const newAtendimento: AtendimentoDtoCreate = {
          tipo: AtendimentoType.Interconsulta,
          unidade: values.unidade ? values.unidade?.value : undefined,
          modalidade: values.modalidade,
          especialidade: values.especialidade?.value,
          endereco: undefined,
          tipoNotificacao: values.tipoNotificacao,
          lotacaoSolicitante: exigeLotacao ? values.lotacaoSolicitante?.value : undefined,
        }
        const atendimentoCriado = await abrirInterconsulta(pacienteCriado, newAtendimento)
        const atendimento = await getAtendimentoById(atendimentoCriado.id)

        if (!atendimento?.lotacaoSolicitante) {
          showToast({
            variant: 'warning',
            title: 'Interconsulta aberta sem lotação do solicitante',
            description: `Protocolo ${
              atendimento?.protocolo ?? ''
            }: o PDF e o relatório da Secretaria de Saúde vão sair sem CBO e INE do solicitante. O motivo está nos logs do atendimento. ${CAMINHO_CADASTRO_LOTACAO}`,
            duration: null,
          })
        }

        const formularios = formulariosEmAberto(atendimento?.formularios)
        if (!formularios.length) {
          // A interconsulta já existe; sem formulário não há o que preencher aqui — avisar em vez
          // de deixar o modal parado.
          closeModal()
          showAlert({
            title: `Interconsulta ${atendimento?.protocolo ?? ''} criada`,
            text:
              'Mas não há formulário de solicitação para preencher: nenhum formulário de ' +
              'interconsulta está configurado para esta instituição e especialidade. Peça ao ' +
              'administrador para revisar os formulários.',
            icon: 'warning',
          })
          return
        }

        const abrirFormulario = (formulario: FormularioAtendimentoList) =>
          showModal({
            title: `${formulario.formulario.nome} - ${atendimento.protocolo}`,
            content: (
              <FormularioModal
                atendimentoId={Number(atendimento.id)}
                isInterconsulta
                enviar={enviarFormularioSolicitacao}
              />
            ),
            fetchers: {
              formularioAtendimento: () => getFormularioAtendimento(atendimento.id, formulario.id),
              formularioResolvido: () => getFormularioResolvido(atendimento.id, formulario.id),
            },
          })

        // Com formulário marcado pelo backend, é ele; sem marcado (empresa sem formulário de IC
        // configurado), o solicitante escolhe na lista completa.
        if (formularios.length === 1 || temFormularioMarcado(atendimento?.formularios)) {
          abrirFormulario(formularios[0])
        } else {
          showModal({
            title: 'Selecionar formulário',
            content: (
              <SelecionarFormularioModal formularios={formularios} onSelect={abrirFormulario} />
            ),
          })
        }
      } catch (error) {
        const elegiveis = lotacoesDaAmbiguidade(error)
        if (!elegiveis) return mostrarErroAbertura(error)

        // Nada foi aberto: o paciente já existe, então basta escolher a lotação e enviar de novo.
        setLotacoesElegiveis(elegiveis)
        showAlert({
          title: 'Escolha a sua lotação',
          text:
            'Você tem mais de uma lotação vigente nesta unidade e a interconsulta não foi aberta. ' +
            'Selecione em "Sua lotação (solicitante)" a que deve constar no PDF e no relatório da ' +
            'Secretaria de Saúde, e envie de novo.',
          icon: 'warning',
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  const empresaId = formik.values.empresa?.value
  const unidadeId = formik.values.unidade?.value
  const pessoaId = pessoa?.id

  // Empresa e unidade escolhidas alimentam a busca de lotações (declarada antes do formik);
  // trocar a unidade descarta a lotação escolhida e as elegíveis do 422, que são da unidade anterior.
  const { setFieldValue: setValorCampo } = formik
  useEffect(() => {
    setEmpresaSelecionada(empresaId)
  }, [empresaId])
  useEffect(() => {
    setUnidadeSelecionada(unidadeId)
    setLotacoesElegiveis(undefined)
    setValorCampo('lotacaoSolicitante', undefined)
  }, [unidadeId, setValorCampo])

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (empresaId) {
          let paciente = null

          if (pessoaId !== undefined) {
            try {
              paciente = await getPacienteByPessoaId(empresaId, pessoaId)
            } catch (err) {
              paciente = null
            }

            if (paciente && paciente.id) setPacienteCriado(Number(paciente.id))
          }
          const resp = await getUnidades(`page=0&size=200&search=&idEmpresa=${empresaId}`)

          setOptionsUnidades(OptionsHelper.createOptions(resp.data || [], 'nome', 'id'))
        }
      } catch (error) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [empresaId, pessoaId])

  const pacienteId = useMemo(() => ({ pacienteId: pacienteCriado }), [pacienteCriado])
  const tipoAtendimento = useMemo(() => ({ tipo: AtendimentoType.Interconsulta }), [])

  const mustHavePhone =
    formik.values.tipoNotificacao === NotificacaoType.Ambos ||
    formik.values.tipoNotificacao === NotificacaoType.Whatsapp

  const telefoneUsuario = currentUser?.pessoa?.telefone
  const shouldFillTelefone = mustHavePhone && !telefoneUsuario

  const { setFieldValue } = formik
  useEffect(() => {
    if (mustHavePhone && telefoneUsuario) setFieldValue('telefone', telefoneUsuario)
  }, [mustHavePhone, telefoneUsuario, setFieldValue])

  return (
    <>
      {!isLoading && info?.documento.numero && !pacienteCriado && formik.values.empresa && (
        <div className='alert alert-warning'>
          <div className='alert-text font-weight-bold'>Paciente não possui cadastro.</div>
        </div>
      )}
      {!isLoading && pacienteCriado && (
        <div className='alert alert-success'>
          <div className='alert-text font-weight-bold'>
            {pacienteCriado && pessoa
              ? 'Paciente já possui cadastro.'
              : 'Paciente criado com sucesso.'}
          </div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        <FormSelect
          formik={formik}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          name='empresa'
          entity='empresa'
          isClearable={false}
          isRequired
          className='mb-3'
          isDisabled={!!pacienteCriado}
          fetchOptionsParams={tipoAtendimento}
        />

        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='primeiroNome'
              label='Primeiro Nome'
              placeholder='Digite o primeiro nome'
              isRequired
              isDisabled={!!pessoa || !!pacienteCriado}
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='sobreNome'
              label='Sobrenome'
              placeholder='Digite o sobrenome'
              isRequired
              isDisabled={!!pessoa || !!pacienteCriado}
            />
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormSelect
              formik={formik}
              label='Documento'
              name='documento'
              placeholder='Selecione um tipo de documento'
              fetchOptions={OptionsHelper.getOptionsTiposDocumentosPessoal}
              isClearable={false}
              isRequired
              isDisabled={!!pessoa || !!pacienteCriado}
            />
          </div>
          <div className='col-md-6'>
            {formik.values.documento?.value === DocumentoPessoaType.Cpf ? (
              <FormInput
                key={formik.values.documento.value}
                formik={formik}
                label={formik.values.documento.label}
                name='cpf'
                placeholder={formik.values.documento.label}
                mask={MaskType.CPF}
                isRequired
                isDisabled={!!pessoa || !!pacienteCriado}
              />
            ) : formik.values.documento?.value === DocumentoPessoaType.Cns ? (
              <FormInput
                key={formik.values.documento.value}
                formik={formik}
                label={formik.values.documento.label}
                name='cns'
                placeholder={formik.values.documento.label}
                mask={MaskType.CNS}
                isRequired
                isDisabled={!!pessoa || !!pacienteCriado}
              />
            ) : formik.values.documento?.value === DocumentoPessoaType.Passaporte ? (
              <FormInput
                key={formik.values.documento.value}
                formik={formik}
                label={formik.values.documento.label}
                name='passaporte'
                placeholder={formik.values.documento.label}
                isRequired
                isDisabled={!!pessoa || !!pacienteCriado}
              />
            ) : formik.values.documento?.value === DocumentoPessoaType.BilheteIdentidadeAngola ? (
              <FormInput
                key={formik.values.documento.value}
                formik={formik}
                label={formik.values.documento.label}
                name='bilheteIdentidadeAngola'
                placeholder={formik.values.documento.label}
                isRequired
                isDisabled={!!pessoa || !!pacienteCriado}
              />
            ) : null}
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormSelect
              formik={formik}
              options={OptionsHelper.generos}
              name='sexo'
              label='Gênero'
              placeholder='Selecione um gênero'
              isClearable={false}
              isRequired
              isDisabled={!!pessoa || !!pacienteCriado}
            />
          </div>
          <div className='col-md-6'>
            <FormDateInput
              formik={formik}
              placeholder='Data de Nascimento'
              name='nascimento'
              label='Data de Nascimento'
              maxDate={new Date()}
              isRequired
              isDisabled={!!pessoa || !!pacienteCriado}
            />
          </div>
        </div>

        {pacienteCriado && (
          <>
            <FormSelect
              formik={formik}
              fetchOptions={OptionsHelper.getOptionsEspecialidadesByPaciente}
              name='especialidade'
              label='Especialidade'
              isRequired
              fetchOptionsParams={pacienteId}
              isClearable={false}
              placeholder='Selecione uma especialidade'
              className='mb-3'
            />

            {optionsUnidades && optionsUnidades.length > 0 && (
              <FormSelect
                formik={formik}
                options={optionsUnidades}
                name='unidade'
                label='Unidade/Setor'
                isClearable={false}
                placeholder='Selecione uma unidade'
                className='mb-3'
              />
            )}
            {exigeLotacao && (
              <FormSelect
                formik={formik}
                options={opcoesLotacao}
                name='lotacaoSolicitante'
                label='Sua lotação (solicitante)'
                isRequired
                isClearable={false}
                placeholder='Selecione a lotação'
                infoText='Você tem mais de uma lotação vigente nesta unidade. Escolha a que identifica você como solicitante nesta interconsulta (CBO e equipe do relatório da Secretaria de Saúde).'
                className='mb-3'
              />
            )}
            {semLotacao && (
              <InfoAlert
                variant='warning'
                size='sm'
                role='status'
                title={`Você não tem lotação vigente ${
                  unidadeSelecionada ? 'nesta unidade' : 'nesta instituição'
                }.`}
              >
                Sem ela, a interconsulta sai sem CBO e INE do solicitante no PDF e no relatório da
                Secretaria de Saúde, ou é recusada se a instituição exigir.{' '}
                {CAMINHO_CADASTRO_LOTACAO}
              </InfoAlert>
            )}
            {lotacoesIndisponiveis && !lotacoesElegiveis && (
              <InfoAlert
                variant='warning'
                size='sm'
                role='status'
                title='Não foi possível consultar suas lotações.'
              >
                Se você tiver mais de uma lotação vigente nesta unidade, vamos pedir que escolha a
                lotação do solicitante ao criar o atendimento.
              </InfoAlert>
            )}
            <FormRadio<NotificacaoType | null>
              formik={formik}
              name='tipoNotificacao'
              label='Você gostaria de receber uma notificação?'
              isDisabled={formik.isSubmitting}
              options={notificacaoOptions}
              isRequired
              className='mb-3'
            />
            {shouldFillTelefone && (
              <div className='col-md-6'>
                <FormInput
                  formik={formik}
                  name='telefone'
                  label='Telefone'
                  placeholder='(00) 0 0000-0000'
                  mask={MaskType.Phone}
                  isRequired
                />
              </div>
            )}
          </>
        )}

        <div className='d-flex justify-content-center mt-10'>
          <Button
            type={ButtonType.Reset}
            onClick={closeModal}
            className='btn-secondary me-3'
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>

          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? (
              <LoadingIndicator />
            ) : pacienteCriado ? (
              'Criar atendimento'
            ) : (
              'Criar paciente'
            )}
          </Button>
        </div>

        {formik.isSubmitting && <ListLoading />}
      </form>
    </>
  )
}
